import React from 'react';
import { useModal } from '../context/ModalContext';

const PitchDeckTips: React.FC = () => {
  const { isPitchDeckTipsOpen, closePitchDeckTips } = useModal();

  if (!isPitchDeckTipsOpen) return null;

  const structureItems = [
    {
      title: "Problem",
      description: "Clearly define the issue you're addressing",
      icon: "🎯"
    },
    {
      title: "Solution",
      description: "Present your product or service as the answer",
      icon: "💡"
    },
    {
      title: "Market",
      description: "Highlight the size and potential",
      icon: "🌍"
    },
    {
      title: "Product",
      description: "Showcase your offering with visuals",
      icon: "✨"
    },
    {
      title: "Traction",
      description: "Provide evidence of growth",
      icon: "📈"
    },
    {
      title: "Team",
      description: "Introduce the people behind the venture",
      icon: "👥"
    },
    {
      title: "Financials",
      description: "Outline your business model",
      icon: "💰"
    },
    {
      title: "Vision",
      description: "Share your long-term goals",
      icon: "🚀"
    }
  ];

  const tips = [
    {
      title: "Design with Clarity",
      icon: "🎨",
      description: "Your deck's design should enhance understanding, not distract.",
      items: [
        "Use large, legible fonts and consistent formatting",
        "Limit each slide to one main idea",
        "Incorporate visuals like charts or screenshots",
        "Avoid clutter; embrace white space for readability"
      ]
    },
    {
      title: "Engage Your Audience",
      icon: "🎭",
      description: "First impressions matter. Start with a strong hook.",
      items: [
        "Present a compelling statistic or fact",
        "Share a brief, relatable story",
        "Pose a thought-provoking question",
        "Set the tone for your presentation"
      ]
    },
    {
      title: "Highlight Traction",
      icon: "📊",
      description: "Demonstrating progress validates your business.",
      items: [
        "Show user growth, revenue, or partnerships",
        "Include charts to visualize trends",
        "Be honest about challenges and solutions",
        "Focus on key metrics that matter"
      ]
    },
    {
      title: "Tailor Your Content",
      icon: "🎯",
      description: "Understand who you're presenting to and customize accordingly.",
      items: [
        "Investors: Focus on return potential and scalability",
        "Customers: Emphasize benefits and user experience",
        "Partners: Highlight collaboration opportunities",
        "Align with audience interests and concerns"
      ]
    },
    {
      title: "Practice & Refine",
      icon: "🎬",
      description: "A well-delivered pitch can make a significant difference.",
      items: [
        "Rehearse multiple times to build confidence",
        "Seek feedback from peers or mentors",
        "Be prepared to answer questions succinctly",
        "Time your presentation appropriately"
      ]
    }
  ];

  const resources = {
    title: "Want to Go Deeper?",
    icon: "📚",
    description: "Check out these resources from Y Combinator:",
    links: [
      {
        url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
        text: "→ How to Build Your Seed Round Pitch Deck"
      },
      {
        url: "https://www.ycombinator.com/library/4T-how-to-design-a-better-pitch-deck",
        text: "→ How to Design a Better Pitch Deck"
      }
    ]
  };

  const inspiration = {
    title: "Get Inspired",
    icon: "💫",
    description: "Looking for inspiration? Check out these successful pitch decks.",
    items: [
      "Browse real pitch decks from successful startups",
      "See how others structured their story",
      "Learn from proven fundraising examples",
      "Get ideas for your own presentation"
    ],
    link: {
      url: "https://bestpitchdeck.com/",
      text: "Browse successful pitch deck examples →"
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pt-20"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closePitchDeckTips();
        }
      }}
    >
      <div 
        className="bg-white rounded-xl p-8 max-w-6xl w-full max-h-[90vh] overflow-y-auto relative scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-10">
          <div className="relative">
            <div className="flex flex-col items-center text-center mb-6">
              <h2 className="text-4xl font-bold text-gray-900 mb-3">
                Pitch Deck Best Practices
              </h2>
              <div className="h-1 w-24 bg-[#57547B] rounded-full mb-4"></div>
              <p className="text-gray-600 text-lg max-w-2xl">Essential tips to create an impactful pitch deck</p>
            </div>
            <button
              onClick={closePitchDeckTips}
              className="absolute right-0 top-0 text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* All Tips Section */}
        <div className="space-y-8">
          {/* Structure Your Story */}
          <div className="bg-[#57547B]/5 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">📝</span>
              <h3 className="text-2xl font-semibold text-gray-900">Structure Your Story</h3>
            </div>
            <p className="text-gray-600 mb-6">A compelling pitch deck should follow a clear narrative with these essential slides:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {structureItems.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-2"
                >
                  <span className="text-[#57547B] flex-shrink-0">{item.icon}</span>
                  <div>
                    <h4 className="font-medium text-gray-900">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Other Tips */}
          <div className="space-y-8">
            {/* First row - Design and Tailor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[tips[0], tips[3]].map((tip, index) => (
                <div 
                  key={index}
                  className="bg-[#57547B]/5 rounded-xl p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{tip.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {tip.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">{tip.description}</p>
                  <ul className="space-y-3">
                    {tip.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <span className="text-[#57547B] mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Second row - Other two tips */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[tips[1], tips[2]].map((tip, index) => (
                <div 
                  key={index}
                  className="bg-[#57547B]/5 rounded-xl p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{tip.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {tip.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">{tip.description}</p>
                  <ul className="space-y-3">
                    {tip.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <span className="text-[#57547B] mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Practice & Refine section */}
            <div className="bg-[#57547B]/5 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{tips[4].icon}</span>
                <h3 className="text-2xl font-semibold text-gray-900">
                  {tips[4].title}
                </h3>
              </div>
              <p className="text-gray-600 mb-6">{tips[4].description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-[#57547B] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#57547B] text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Rehearse</h4>
                    <p className="text-gray-600 text-sm">Practice to build confidence</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-[#57547B] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#57547B] text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Get Feedback</h4>
                    <p className="text-gray-600 text-sm">Learn from peers and mentors</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-[#57547B] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#57547B] text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Prepare Q&A</h4>
                    <p className="text-gray-600 text-sm">Be ready for questions</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-[#57547B] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#57547B] text-sm">4</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Time It</h4>
                    <p className="text-gray-600 text-sm">Stay within time limits</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resources and Inspiration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          {/* Resources */}
          <div className="bg-[#57547B]/5 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{resources.icon}</span>
              <h3 className="text-xl font-semibold text-gray-900">{resources.title}</h3>
            </div>
            <p className="text-gray-600 mb-4">{resources.description}</p>
            <ul className="space-y-3">
              {resources.links.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#57547B] hover:text-[#6a6a8a] transition-colors flex items-center gap-1"
                  >
                    {link.text}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Inspiration */}
          <div className="bg-[#57547B]/5 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{inspiration.icon}</span>
              <h3 className="text-xl font-semibold text-gray-900">{inspiration.title}</h3>
            </div>
            <p className="text-gray-600 mb-4">{inspiration.description}</p>
            <ul className="space-y-3 mb-4">
              {inspiration.items.map((item, index) => (
                <li key={index} className="flex items-center">
                  <span className="text-[#57547B] mr-2 flex-shrink-0">•</span>
                  <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <a 
              href={inspiration.link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#57547B] hover:text-[#6a6a8a] transition-colors flex items-center gap-1"
            >
              {inspiration.link.text}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchDeckTips; 